/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import ActionMgr, { Action } from './ActionMgr'
import API, { Customer, CustomersResponse, Group, GroupCollection, GroupCollectionsResponse, GroupsResponse, RoleTemplate, SecondaryCustomer, SSORoleTemplate, SSORoleTemplatesResponse, SystemRole, SystemTemplate, SystemTemplateResponse, TokaraCustomer, User, UsersResponse } from './API'

export interface SSORole {
  name: string;
  category: string;
  internal: boolean;
  ssoRole: string;
  desc: string;
}

export class DataMgrImpl {
  private _customers = new Map<number, Customer>()
  async reloadCustomers () {
    let nextPageToken
    this._customers.clear()
    while (true) {
      const resp: CustomersResponse | null = await API.getAdminCustomers(nextPageToken)
      if (resp && Array.isArray(resp.customers)) {
        resp.customers.forEach((c) => {
          this._customers.set(c.id, c)
        })
        if (typeof resp.next_page_token === 'string' && resp.next_page_token !== '') {
          nextPageToken = resp.next_page_token
          // break
        } else {
          break // No More
        }
      } else {
        ActionMgr.triggerAction(Action.CustomersUpdated)
        return null
      }
    }
    ActionMgr.triggerAction(Action.CustomersUpdated)
    return this._customers
  }

  private _users = new Map<string, User>()
  async reloadUsers () {
    let nextPageToken
    this._users.clear()
    while (true) {
      const resp: UsersResponse | null = await API.getAdminUsers(nextPageToken)
      if (resp && Array.isArray(resp.users)) {
        resp.users.forEach((c) => {
          this._users.set(c.sub, c)
        })
        if (typeof resp.next_page_token === 'string' && resp.next_page_token !== '') {
          nextPageToken = resp.next_page_token
          // break
        } else {
          break // No More
        }
      } else {
        ActionMgr.triggerAction(Action.UsersUpdated)
        return null
      }
    }
    ActionMgr.triggerAction(Action.UsersUpdated)
    return this._users
  }

  private _systemTemplates = new Map<number, SystemTemplate>()
  async reloadSystemTemplates () {
    let nextPageToken
    this._systemTemplates.clear()
    while (true) {
      const resp: SystemTemplateResponse | null = await API.getAdminTemplates(nextPageToken)
      if (resp && Array.isArray(resp.templates)) {
        resp.templates.forEach((c) => {
          this._systemTemplates.set(c.id || 0, c)
        })
        if (typeof resp.next_page_token === 'string' && resp.next_page_token !== '') {
          nextPageToken = resp.next_page_token
          // break
        } else {
          break // No More
        }
      } else {
        ActionMgr.triggerAction(Action.SystemTemplatesUpdated)
        return null
      }
    }
    ActionMgr.triggerAction(Action.SystemTemplatesUpdated)
    return this._systemTemplates
  }

  private _tokaraCustomers = new Map<number, TokaraCustomer>()
  async reloadTokaraCustomers () {
    const resp = await API.getTokaraCustomers()
    this._tokaraCustomers.clear()
    if (resp && resp.data && resp.data.customers) {
      resp.data.customers.forEach((c) => {
        this._tokaraCustomers.set(c.id, c)
      })
    }
    return this._tokaraCustomers
  }

  private _groups = new Map<number, Group>()
  async reloadGroups () {
    let nextPageToken
    this._groups.clear()
    while (true) {
      const resp: GroupsResponse | null = await API.getAdminGroups(nextPageToken)
      if (resp && Array.isArray(resp.groups)) {
        resp.groups.forEach((c) => {
          this._groups.set(c.id, c)
        })
        if (typeof resp.next_page_token === 'string' && resp.next_page_token !== '') {
          nextPageToken = resp.next_page_token
          // break
        } else {
          break // No More
        }
      } else {
        ActionMgr.triggerAction(Action.GroupsUpdated)
        return null
      }
    }
    ActionMgr.triggerAction(Action.GroupsUpdated)
    return this._groups
  }

  private _groupCollections = new Map<number, GroupCollection>()
  async reloadGroupCollections () {
    let nextPageToken
    this._groupCollections.clear()
    while (true) {
      const resp: GroupCollectionsResponse | null = await API.getAdminGroupCollections(nextPageToken)
      if (resp && Array.isArray(resp.group_collections)) {
        resp.group_collections.forEach((c) => {
          this._groupCollections.set(c.id, c)
        })
        if (typeof resp.next_page_token === 'string' && resp.next_page_token !== '') {
          nextPageToken = resp.next_page_token
          // break
        } else {
          break // No More
        }
      } else {
        ActionMgr.triggerAction(Action.GroupCollectionsUpdated)
        return null
      }
    }
    ActionMgr.triggerAction(Action.GroupCollectionsUpdated)
    return this._groupCollections
  }

  private _ssoRoleTemplates = new Map<number, SSORoleTemplate>()
  async reloadSSORoleTemplates () {
    let nextPageToken
    this._ssoRoleTemplates.clear()
    while (true) {
      const resp: SSORoleTemplatesResponse | null = await API.getAdminSSORoleTemplates(nextPageToken)
      if (resp && Array.isArray(resp.role_templates)) {
        resp.role_templates.forEach((c) => {
          this._ssoRoleTemplates.set(c.id, c)
        })
        if (typeof resp.next_page_token === 'string' && resp.next_page_token !== '') {
          nextPageToken = resp.next_page_token
          // break
        } else {
          break // No More
        }
      } else {
        ActionMgr.triggerAction(Action.SSORoleTemplatesUpdated)
        return null
      }
    }
    ActionMgr.triggerAction(Action.SSORoleTemplatesUpdated)
    return this._ssoRoleTemplates
  }

  async loadCurrentUser () {
    const userResp = await API.getUser()
    if (userResp && userResp.user) {
      const ids: Array<number> = []
      if (userResp.user.customer_id) {
        ids.push(userResp.user.customer_id)
      }
      const secVals = userResp.user as Record<string, SecondaryCustomer | undefined>
      for (let idx = 1; idx <= 9; ++idx) {
        const sv = secVals['secondary_customer_' + idx]
        if (sv && sv.id) {
          ids.push(sv.id)
        }
      }
      if (ids.length > 0) {
        const custVals = await Promise.all(ids.map((i) => API.getCustomer(i)))
        for (const c of custVals) {
          if (c && c.customer) {
            this._customers.set(c.customer.id, c.customer)
          }
        }
      }
      this._users.set(userResp.user.sub, userResp.user)
      return userResp.user
    } else {
      return null
    }
  }

  private _systemRoles = new Array<SystemRole>()
  private _systemSSORoles = new Array<SSORole>()
  async reloadSystemRoles () {
    this._systemRoles = []
    const resp = await API.getAdminRoles()
    if (resp && Array.isArray(resp.roles)) {
      this._systemRoles = resp.roles
      this._systemSSORoles = []
      const knownRoles = this.getRoles()
      const knownRolesSet = new Set<string>(knownRoles.map((r) => r.ssoRole))
      this._systemRoles.forEach((r) => {
        if (!knownRolesSet.has(r.name)) {
          const ssoRole = this.createRole(r.name, r.description)
          this._systemSSORoles.push(ssoRole)
        }
      })
      if (this._systemSSORoles.length > 0) {
        console.log('Note: There are ' + this._systemSSORoles.length + ' roles not recognized by the portal: ' + this._systemSSORoles.map((r) => r.ssoRole).join(','))
      }
    }
    return this._systemRoles
  }

  getCustomers () { return this._customers }
  getUsers () { return this._users }
  getSystemTemplates () { return this._systemTemplates }
  getGroups () { return this._groups }
  getGroupCollections () { return this._groupCollections }
  getSSORoleTemplates () { return this._ssoRoleTemplates }
  getTokaraCustomers () { return this._tokaraCustomers }

  getRoles () {
    const roles: Array<SSORole> = [
      { name: 'Readonly', category: 'SSO Admin Portal', internal: true, ssoRole: 'sso_admin_readonly', desc: 'Read only internal any company' },
      { name: 'Readwrite', category: 'SSO Admin Portal', internal: true, ssoRole: 'sso_admin_readwrite', desc: 'Read/edit – internal all companies' },
      { name: 'Superuser', category: 'SSO Admin Portal', internal: true, ssoRole: 'sso_admin_superuser', desc: 'Read/edit/delete – all companies' },
      { name: 'Readonly', category: 'SSO Customer Portal', internal: false, ssoRole: 'sso_cust_readonly', desc: 'Read only – external user for the one company' },
      { name: 'Readwrite', category: 'SSO Customer Portal', internal: false, ssoRole: 'sso_cust_readwrite', desc: 'Read/edit – external user for the one company' },
      { name: 'Superuser', category: 'SSO Customer Portal', internal: false, ssoRole: 'sso_cust_superuser', desc: 'Read/edit/delete – external user for the one company' },
      { name: 'Manager', category: 'ADR Alerts', internal: false, ssoRole: 'adr_alerts_customerman', desc: 'User/Viewer' },
      { name: 'Admin', category: 'ADR Alerts', internal: true, ssoRole: 'adr_alerts_intadmin', desc: 'Internal Admin' },
      { name: 'Superuser', category: 'ADR Alerts', internal: true, ssoRole: 'adr_alerts_intsuperuser', desc: 'ADR Team' },
      { name: 'Support', category: 'ADR Alerts', internal: true, ssoRole: 'adr_alerts_intsupport', desc: 'ADR Team' },
      { name: 'Manager', category: 'ADR Report', internal: false, ssoRole: 'adr_report_customerman', desc: 'User/Viewer' },
      { name: 'Admin', category: 'ADR Report', internal: true, ssoRole: 'adr_report_intadmin', desc: 'Internal Admin' },
      { name: 'Superuser', category: 'ADR Report', internal: true, ssoRole: 'adr_report_intsuperuser', desc: 'ADR Team' },
      { name: 'Support', category: 'ADR Report', internal: true, ssoRole: 'adr_report_intsupport', desc: 'ADR Team' },
      { name: 'Manager', category: 'ADR Rinex', internal: false, ssoRole: 'adr_rinex_customerman', desc: 'User/Viewer' },
      { name: 'Admin', category: 'ADR Rinex', internal: true, ssoRole: 'adr_rinex_intadmin', desc: 'Internal Admin' },
      { name: 'Superuser', category: 'ADR Rinex', internal: true, ssoRole: 'adr_rinex_intsuperuser', desc: 'ADR Team' },
      { name: 'Support', category: 'ADR Rinex', internal: true, ssoRole: 'adr_rinex_intsupport', desc: 'ADR Team' },
      { name: 'Manager', category: 'ADR Rtmap', internal: false, ssoRole: 'adr_rtmap_customerman', desc: 'User/Viewer' },
      { name: 'Admin', category: 'ADR Rtmap', internal: true, ssoRole: 'adr_rtmap_intadmin', desc: 'Internal Admin' },
      { name: 'Superuser', category: 'ADR Rtmap', internal: true, ssoRole: 'adr_rtmap_intsuperuser', desc: 'ADR Team' },
      { name: 'Support', category: 'ADR Rtmap', internal: true, ssoRole: 'adr_rtmap_intsupport', desc: 'ADR Team' },
      { name: 'Manager', category: 'ADR Support', internal: false, ssoRole: 'adr_support_customerman', desc: 'User/Viewer' },
      { name: 'Admin', category: 'ADR Support', internal: true, ssoRole: 'adr_support_intadmin', desc: 'Internal Admin' },
      { name: 'Superuser', category: 'ADR Support', internal: true, ssoRole: 'adr_support_intsuperuser', desc: 'ADR Team' },
      { name: 'Support', category: 'ADR Support', internal: true, ssoRole: 'adr_support_intsupport', desc: 'ADR Team' },
      { name: 'Customer Admin', category: 'ADR WebAcc', internal: false, ssoRole: 'adr_webacc_customeradmin', desc: 'Bussiness contact, billing' },
      { name: 'Customer Contact', category: 'ADR WebAcc', internal: false, ssoRole: 'adr_webacc_customercont', desc: 'Guest with limited view' },
      { name: 'Customer Employee', category: 'ADR WebAcc', internal: false, ssoRole: 'adr_webacc_customerempl', desc: 'User/Viewer' },
      { name: 'Manager', category: 'ADR WebAcc', internal: false, ssoRole: 'adr_webacc_customerman', desc: 'Operations Manager' },
      { name: 'Accounts', category: 'ADR WebAcc', internal: true, ssoRole: 'adr_webacc_intaccounts', desc: 'Accounts - Regional Sales Teams (previously Accounts)' },
      { name: 'Admin', category: 'ADR WebAcc', internal: true, ssoRole: 'adr_webacc_intadmin', desc: 'Internal Admin - Subscriptions Team (previously Accounts Admin)' },
      { name: 'Support Admin', category: 'ADR WebAcc', internal: true, ssoRole: 'adr_webacc_intsupadmin', desc: 'Support Admin - regional support manager' },
      { name: 'Superuser', category: 'ADR WebAcc', internal: true, ssoRole: 'adr_webacc_intsuperuser', desc: 'ADR Team' },
      { name: 'Support', category: 'ADR WebAcc', internal: true, ssoRole: 'adr_webacc_intsupport', desc: 'Support' },
      { name: 'Admin', category: 'PP ECommerce', internal: true, ssoRole: 'ppm_ecomm_intadmin', desc: 'internal Staff with full read  access  all items on Ecommerce' },
      { name: 'Support', category: 'PP ECommerce', internal: true, ssoRole: 'ppm_ecomm_intsupport', desc: 'internal Staff with full read write access  all items on Ecommerce' },
      { name: 'Manager', category: 'PP Forum', internal: false, ssoRole: 'ppm_forum_customerman', desc: 'Customer login with forum posting rights' },
      { name: 'Readonly', category: 'PP Forum', internal: false, ssoRole: 'ppm_forum_customerweb', desc: 'Customer login with forum read only' },
      { name: 'Admin', category: 'PP Forum', internal: true, ssoRole: 'ppm_forum_intadmin', desc: 'internal staff with forum edit rights' },
      { name: 'Customer Admin', category: 'PP Wordpress', internal: false, ssoRole: 'ppm_wordpress_customeradmin', desc: 'Customer able to manage logins and access for users with the same company' },
      { name: 'Customer Employee', category: 'PP Wordpress', internal: false, ssoRole: 'ppm_wordpress_customerempl', desc: 'Customer able to manage their own logins  can view company data' },
      { name: 'Readonly', category: 'PP Wordpress', internal: false, ssoRole: 'ppm_wordpress_customerweb', desc: 'Login with view only access' },
      { name: 'Admin', category: 'PP Wordpress', internal: true, ssoRole: 'ppm_wordpress_intadmin', desc: 'internal Staff with full read  access to web site' },
      { name: 'Superuser', category: 'PP Wordpress', internal: true, ssoRole: 'ppm_wordpress_intsuperuser', desc: 'internal Staff with full read write access to Web site' },
      { name: 'Admin', category: 'Tokara Admin Portal', internal: true, ssoRole: 'tka_admin_intadmin', desc: 'PP Staff with view only permission to create-delete-edit users' },
      { name: 'Support Admin', category: 'Tokara Admin Portal', internal: true, ssoRole: 'tka_admin_intsupadmin', desc: 'PPStaff with access to all permissons on all Tokara Platforms' },
      { name: 'Superuser', category: 'Tokara Admin Portal', internal: true, ssoRole: 'tka_admin_intsuperuser', desc: 'PP Staff with view only permission to run privaleged report queries access all diganostics data-functions' },
      { name: 'Customer Admin', category: 'Tokara Customer Portal', internal: false, ssoRole: 'tka_cust_customeradmin', desc: 'Customer able to manage logins and access for users with the same company, can connect and trasfer data to remote devices ' },
      { name: 'Customer Contact', category: 'Tokara Customer Portal', internal: false, ssoRole: 'tka_cust_customercont', desc: 'Customer not able to view remote devices, can send data files' },
      { name: 'Customer Employee', category: 'Tokara Customer Portal', internal: false, ssoRole: 'tka_cust_customerempl', desc: 'Customer able to manage their own logins  can connect and transfer data to remote devices ' },
      { name: 'Customer Manager', category: 'Tokara Customer Portal', internal: false, ssoRole: 'tka_cust_customerman', desc: 'Customer able to manage their own logins  can view company data can connect and transfer data to remote devices ' },
      { name: 'Customer Superuser', category: 'Tokara Customer Portal', internal: false, ssoRole: 'tka_cust_extsuperuser', desc: 'Customer able to manage logins can view company data can connect and transfer data to remote devices including GPS Controllers' },
      { name: 'Admin', category: 'Tokara Customer Portal', internal: true, ssoRole: 'tka_cust_intadmin', desc: 'PP Staff able to see all log files/meta data  for a customer device' },
      { name: 'Support Admin', category: 'Tokara Customer Portal', internal: true, ssoRole: 'tka_cust_intsupadmin', desc: 'PP Staff able to see all log files/meta data  for a customer device' },
      { name: 'Manager', category: 'Tokara Subscription Portal', internal: false, ssoRole: 'tka_sub_customerman', desc: 'Customer login with access to the Sub portal for there company' },
      { name: 'Admin', category: 'Tokara Subscription Portal', internal: true, ssoRole: 'tka_sub_intadmin', desc: 'PP Staff with create-edit-disable permission' },
      { name: 'Superuser', category: 'Tokara Subscription Portal', internal: true, ssoRole: 'tka_sub_intsuperuser', desc: 'PP Staff with create-delete-edit-disable permission' },
      { name: 'Support', category: 'Tokara Subscription Portal', internal: true, ssoRole: 'tka_sub_intsupport', desc: 'PP Staff with view only permission for all subs' },
      { name: 'Admin', category: 'Tokara Support Portal', internal: true, ssoRole: 'tka_supp_intadmin', desc: 'PP Staff setup and edit users admin portal, can access and customers machines' },
      { name: 'Superuser', category: 'Tokara Support Portal', internal: true, ssoRole: 'tka_supp_intsuperuser', desc: 'PP Staff setup and edit users in the support/SSO portal including user transfers can access and customers machines' },
      { name: 'Customer Admin', category: 'Tokara R3M Portal', internal: false, ssoRole: 'tka_r3m_customeradmin', desc: 'Customer able to manage logins and access for users with the same company, can connect and trasfer data to remote devices ' },
      { name: 'Customer Support Admin', category: 'Tokara R3M Portal', internal: false, ssoRole: 'tka_r3m_customersupadmin', desc: 'Customer able to manage logins and access for users with the same company, can connect and trasfer data to remote devices ' },
      { name: 'Customer Contact', category: 'Tokara R3M Portal', internal: false, ssoRole: 'tka_r3m_customercont', desc: 'Customer not able to view remote devices, can send data files' },
      { name: 'Customer Employee', category: 'Tokara R3M Portal', internal: false, ssoRole: 'tka_r3m_customerempl', desc: 'Customer able to manage their own logins can connect and transfer data to remote devices ' },
      { name: 'Customer Manager', category: 'Tokara R3M Portal', internal: false, ssoRole: 'tka_r3m_customerman', desc: 'Customer able to manage their own logins can view company data can connect and transfer data to remote devices ' },
      { name: 'Admin', category: 'Tokara R3M Portal', internal: true, ssoRole: 'tka_r3m_intadmin', desc: 'PP Staff able to see all log files/meta data for a customer device' },
      { name: 'Support Admin', category: 'Tokara R3M Portal', internal: true, ssoRole: 'tka_r3m_intsupadmin', desc: 'PP Staff able to see all log files/meta data for a customer device' },
      { name: 'Superuser', category: 'Tokara R3M Portal', internal: true, ssoRole: 'tka_r3m_intsuperuser', desc: 'PP Staff setup and edit users in the support/SSO portal including user transfers can access and customers machines' },
      { name: 'Manager', category: 'Report Haul Truck', internal: false, ssoRole: 'rep_haultruck_manager', desc: 'External role – user cant edit but can view full report' },
      { name: 'Admin', category: 'Report Haul Truck', internal: false, ssoRole: 'rep_haultruck_admin', desc: 'External role – user has full access' },
      { name: 'Internal Admin', category: 'Report Haul Truck', internal: true, ssoRole: 'rep_haultruck_intadmin', desc: 'Internal Role - Used for haultruck customer and project setup' },
      { name: 'User', category: 'Report Haul Truck', internal: false, ssoRole: 'rep_haultruck_user', desc: 'External role – read only. Can only view tonnage moved' }
    ]
    return roles.concat(this._systemSSORoles)
  }

  getRoleCategory (roleItems: [string, string, string]) {
    // This works for some, but we have a few exceptions
    let prefix = roleItems[0].toUpperCase()
    switch (roleItems[0]) {
      case 'tka': prefix = 'Tokara'; break
      case 'ppm': prefix = 'PP'; break
      case 'rep': prefix = 'Report'; break
    }
    // This works for most, but we have a few exceptions
    let suffix = roleItems[1].slice(0, 1).toUpperCase() + roleItems[1].slice(1)
    switch (roleItems[1]) {
      case 'cust': suffix = 'Customer Portal'; break
      case 'supp': suffix = 'Support Portal'; break
      case 'sub': suffix = 'Subscription Portal'; break
      case 'admin': suffix = 'Admin Portal'; break
      case 'ecomm': suffix = 'ECommerce'; break
      case 'webacc': suffix = 'WebAcc'; break
      case 'r3m': suffix = 'R3M Portal'; break
      case 'haultruck': suffix = 'Haul Truck'; break
    }
    return prefix + ' ' + suffix
  }

  getRoleName (roleItems: [string, string, string]) {
    const roleWOInt = roleItems[2].replace(/^int/, '')
    // This works for most, but we have a few exceptions
    let ret = roleWOInt.slice(0, 1).toUpperCase() + roleWOInt.slice(1)
    switch (roleWOInt) {
      case 'customerman': ret = 'Customer Manager'; break
      case 'customerempl': ret = 'Customer Employee'; break
      case 'customercont': ret = 'Customer Contact'; break
      case 'customeradmin': ret = 'Customer Admin'; break
      case 'customersupadmin': ret = 'Customer Support Admin'; break
      case 'extsuperuser': ret = 'Customer Superuser'; break
      case 'customerweb': ret = 'Readonly'; break
      case 'supadmin': ret = 'Support Admin'; break
    }
    if (this.isRoleInternal(roleItems)) {
      ret = 'Internal ' + ret
    }
    return ret
  }

  isRoleInternal (roleItems: [string, string, string]) {
    // Pretty much all internal roles start with 'int', but SSO Admin Portal is always internal
    return roleItems[2].startsWith('int') || (roleItems[0] === 'sso' && roleItems[1] === 'admin')
  }

  createRole (role: string, desc?: string) {
    const roleItems = role.split('_') as [string, string, string]
    if (roleItems.length < 3) return { name: role, category: role, internal: false, ssoRole: role, desc: desc || '' } as SSORole
    return {
      name: this.getRoleName(roleItems),
      category: this.getRoleCategory(roleItems),
      internal: this.isRoleInternal(roleItems),
      ssoRole: role,
      desc: desc || ''
    } as SSORole
  }

  getRole (role: string) {
    const roles = this.getRoles().filter((r) => r.ssoRole === role)
    if (roles.length === 1) return roles[0]
    return this.createRole(role)
  }

  async reloadRoleTemplates () {
    this._roleTemplates = []
    const resp = await API.getTemplates()
    if (resp !== null && Array.isArray(resp.templates)) {
      this._roleTemplates = resp.templates
    }
    ActionMgr.triggerAction(Action.RoleTemplatesUpdated)
    return this._roleTemplates
  }

  _roleTemplates: Array<RoleTemplate> = []
  getRoleTemplates () {
    return this._roleTemplates
  }
}

const DataMgr = new DataMgrImpl()

export default DataMgr
